import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';

import { YapService } from '../services/api';
import { useUserData } from './UserDataContext';
import { DISABLE_ASYNC_EFFECTS } from '../debugConfig';

export interface LikeNotification {
  id: string;
  yapId: string;
  yapAuthor: string;
  yapContent: string;
  timestamp: number;
  read: boolean;
}

// Unified interaction schema
export type PostType = 'yap' | 'shout' | 'forum' | 'tribe';
export type ActionType = 'like' | 'comment' | 'share' | 'shout';

export interface InteractionEvent {
  postId: string;
  postType: PostType;
  authorId: string;
  actionType: ActionType;
}

interface YapInteractionsContextType {
  likedYaps: string[];
  likedShouts: string[];
  shoutedYaps: string[];
  likeNotifications: LikeNotification[];
  toggleLike: (yapId: string, postType?: PostType, yapAuthor?: string, yapContent?: string) => void;
  /** Resolves true when the shout/unshout was actually written. Callers that
   *  optimistically insert a local shout (ProfileScreen) must await this, or a
   *  failed write leaves a phantom shout behind. */
  toggleShout: (yapId: string, caption?: string, images?: string[], audioData?: { url: string, duration: number }, friendsOnly?: boolean) => Promise<boolean>;
  isYapLiked: (yapId: string, postType?: PostType) => boolean;
  isYapShouted: (yapId: string) => boolean;
  getLikeCount: (originalCount: number, yapId: string, postType?: PostType) => number;
  getShoutCount: (originalCount: number, yapId: string) => number;
  markNotificationAsRead: (notificationId: string) => void;
  getUnreadNotificationsCount: () => number;
  handleInteraction: (event: InteractionEvent) => void;
  isPostLiked: (postId: string, postType: PostType) => boolean;
  getPostLikeCount: (originalCount: number, postId: string, postType: PostType) => number;
  getPostReplyCount: (originalCount: number, postId: string, postType?: PostType) => number;
  adjustReplyCount: (postId: string, delta: number, postType?: PostType) => void;
}

const YapInteractionsContext = createContext<YapInteractionsContextType | undefined>(undefined);

export function YapInteractionsProvider({ children }: { children: ReactNode }) {
  // 🔒 HOOKS FIRST: context hooks must come before any useState that depends on their values
  const { userProfile } = useUserData();

  // Initialize directly from userProfile to minimize the race window where likedYaps=[]
  // while userProfile.likedYapIds is already populated. Without this, rapid interactions
  // before the useEffect fires can cause "ghost likes" that produce incorrect count deltas.
  const [likedYaps, setLikedYaps] = useState<string[]>(() => userProfile.likedYapIds || []);
  const [likedShouts, setLikedShouts] = useState<string[]>(() => userProfile.likedShoutIds || []);
  const [shoutedYaps, setShoutedYaps] = useState<string[]>(() => userProfile.shoutedYapIds || []);
  const [likeNotifications, setLikeNotifications] = useState<LikeNotification[]>([]);

  const [localLikeAdjustments, setLocalLikeAdjustments] = useState<Record<string, { delta: number, ts: number, baseCount?: number }>>({});
  const [localCommentAdjustments, setLocalCommentAdjustments] = useState<Record<string, { delta: number, ts: number, baseCount?: number }>>({});
  const [localShoutAdjustments, setLocalShoutAdjustments] = useState<Record<string, { delta: number, ts: number, baseCount?: number }>>({});

  const lastSeenLikeCountRef = useRef<Record<string, number>>({});
  const lastSeenShoutCountRef = useRef<Record<string, number>>({});
  const lastSeenCommentCountRef = useRef<Record<string, number>>({});

  useEffect(() => {
    if (userProfile.likedYapIds) setLikedYaps(userProfile.likedYapIds);
    if (userProfile.likedShoutIds) setLikedShouts(userProfile.likedShoutIds);
    if (userProfile.shoutedYapIds) setShoutedYaps(userProfile.shoutedYapIds);
  }, [userProfile.likedYapIds, userProfile.likedShoutIds, userProfile.shoutedYapIds]);

  useEffect(() => {
    // 1. Existing refresh event listener
    const handleRefresh = ((event: any) => {
      const isForced = event?.detail?.force === true || !event;
      if (isForced) {
        setLocalCommentAdjustments({});
        setLocalLikeAdjustments({});
        setLocalShoutAdjustments({});
      }
    }) as EventListener;
    window.addEventListener('refreshInteractions', handleRefresh);

    return () => {
        window.removeEventListener('refreshInteractions', handleRefresh);
    };
  }, []);

  // Posts with a like/unlike request in flight, keyed by postType too: a yap and a
  // shout are separate documents that can share an id.
  const likeInFlightRef = useRef<Set<string>>(new Set());

  const toggleLike = async (yapId: string, postType: PostType = 'yap', yapAuthor?: string, yapContent?: string) => {
    if ((userProfile as any).status === 'banned') {
      import('sonner').then(({ toast }) => toast.error('Your account has been suspended.'));
      return;
    }

    // Restructured for the same reason as toggleShout: the API call, the local
    // count adjustment and the notification push all used to run INSIDE the
    // setLikedYaps / setLikedShouts updater. React re-invokes updaters (StrictMode
    // does so on every call in development), so each of those fired twice — two
    // like writes and a duplicate entry in likeNotifications per tap. Likes are
    // idempotent server-side, which is the only reason this was survivable where
    // the shout equivalent was not. Intent is resolved once, here; the updaters
    // below only map state.
    const inFlightKey = `${postType}:${yapId}`;
    if (likeInFlightRef.current.has(inFlightKey)) return;

    const isShoutLike = postType === 'shout';
    const currentList = isShoutLike ? likedShouts : likedYaps;
    const setList = isShoutLike ? setLikedShouts : setLikedYaps;

    // 🔒 RACE GUARD: Cross-check against userProfile to catch cases where local state
    // hasn't been seeded yet (component mounted before useEffect fired).
    const profileLiked = (isShoutLike ? userProfile.likedShoutIds : userProfile.likedYapIds)?.includes(yapId) ?? false;
    const isLiking = !currentList.includes(yapId) && !profileLiked;

    likeInFlightRef.current.add(inFlightKey);

    setList((prev) => (
      isLiking
        ? (prev.includes(yapId) ? prev : [...prev, yapId])
        : prev.filter(id => id !== yapId)
    ));

    const baseCount = lastSeenLikeCountRef.current[yapId] || 0;
    setLocalLikeAdjustments(prevAdjust => ({
      ...prevAdjust,
      [yapId]: { delta: isLiking ? 1 : -1, baseCount, ts: Date.now() }
    }));

    if (isLiking && !isShoutLike && yapAuthor && yapContent) {
      const newNotification: LikeNotification = {
        id: `like_${yapId}_${Date.now()}`,
        yapId,
        yapAuthor,
        yapContent: yapContent.substring(0, 100),
        timestamp: Date.now(),
        read: false,
      };
      setLikeNotifications((prevNotifs) => [newNotification, ...prevNotifs]);
    }

    try {
      if (isShoutLike) {
        if (isLiking) await YapService.likeShout(yapId); else await YapService.unlikeShout(yapId);
      } else {
        if (isLiking) await YapService.likeYap(yapId); else await YapService.unlikeYap(yapId);
      }
    } catch (error) {
      console.error('[YapInteractionsContext] toggleLike API failed:', error);
      setLocalLikeAdjustments(prev => {
        const next = { ...prev };
        delete next[yapId];
        return next;
      });
      // Put the toggle itself back too. Previously only the count adjustment was
      // rolled back, so a failed like left the heart filled for something that was
      // never written — and it stayed that way until the next profile sync.
      setList((cur) => (
        isLiking
          ? cur.filter(id => id !== yapId)
          : (cur.includes(yapId) ? cur : [...cur, yapId])
      ));
    } finally {
      likeInFlightRef.current.delete(inFlightKey);
    }
  };

  // Yaps with a shout/unshout request in flight. Unlike a like, a shout is NOT
  // idempotent server-side: the second write hits the "one shout per user per
  // yap" transaction, throws SHOUT_ALREADY_EXISTS, and the error path then undoes
  // a shout that actually succeeded.
  const shoutInFlightRef = useRef<Set<string>>(new Set());

  const toggleShout = async (yapId: string, caption?: string, images?: string[], audioData?: { url: string, duration: number }, friendsOnly?: boolean): Promise<boolean> => {
    if ((userProfile as any).status === 'banned') {
      import('sonner').then(({ toast }) => toast.error('Your account has been suspended.'));
      return false;
    }

    // Every side effect below used to live INSIDE the setShoutedYaps updater.
    // React treats updaters as pure and re-invokes them (StrictMode does so on
    // every call in development), which fired shoutYap twice: the first write
    // succeeded, the second threw SHOUT_ALREADY_EXISTS, and the catch reverted
    // the toggle and showed an error for a shout that had in fact been created.
    // That is why shouting appeared to fail while the shout still turned up on
    // reload. Intent is now resolved once, here, and the updater only maps state.
    if (shoutInFlightRef.current.has(yapId)) return false;

    // Cross-check the profile as well as local state, mirroring toggleLike's race
    // guard: local state can still be empty if the component mounted before the
    // seeding effect ran.
    const profileShouted = userProfile.shoutedYapIds?.includes(yapId) ?? false;
    const isShouting = !shoutedYaps.includes(yapId) && !profileShouted;

    shoutInFlightRef.current.add(yapId);

    setShoutedYaps((prev) => (
      isShouting
        ? (prev.includes(yapId) ? prev : [...prev, yapId])
        : prev.filter(id => id !== yapId)
    ));

    const baseCount = lastSeenShoutCountRef.current[yapId] || 0;
    setLocalShoutAdjustments(prevAdjust => ({
      ...prevAdjust,
      [yapId]: { delta: isShouting ? 1 : -1, baseCount, ts: Date.now() }
    }));

    try {
      if (isShouting) {
        await YapService.shoutYap(yapId, caption, images, audioData, friendsOnly ? 'friends' : 'public');
      } else {
        await YapService.unshoutYap(yapId);
      }
      return true;
    } catch (error) {
      console.error('[YapInteractionsContext] toggleShout API failed:', error);
      // Revert local adjustment on failure
      setLocalShoutAdjustments(prev => {
        const next = { ...prev };
        delete next[yapId];
        return next;
      });
      // ...and put the toggle itself back, otherwise the button keeps showing
      // the shouted state for something that was never written.
      setShoutedYaps((cur) => isShouting ? cur.filter(id => id !== yapId) : (cur.includes(yapId) ? cur : [...cur, yapId]));
      const alreadyShouted = /SHOUT_ALREADY_EXISTS/.test(String((error as any)?.message || ''));
      import('sonner').then(({ toast }) =>
        toast.error(
          alreadyShouted ? "You've already shouted this yap."
            : isShouting ? "Couldn't shout that yap. Try again."
            : "Couldn't remove that shout. Try again."
        )
      );
      return false;
    } finally {
      shoutInFlightRef.current.delete(yapId);
    }
  };

  const isYapLiked = (yapId: string, postType: PostType = 'yap') => isPostLiked(yapId, postType);
  // `shoutedYaps` is updated synchronously by toggleShout, so it is already the
  // authoritative answer — there was previously a branch here that consulted
  // localShoutAdjustments first, but it guarded on an `expectedCount` field that
  // does not exist on those entries and returned this same expression anyway.
  const isYapShouted = (yapId: string) => shoutedYaps.includes(yapId);

  const getLikeCount = (originalCount: number, yapId: string, postType: PostType = 'yap') => getPostLikeCount(originalCount, yapId, postType);
  const getShoutCount = (originalCount: number, yapId: string) => {
    lastSeenShoutCountRef.current[yapId] = originalCount;
    const localItem = localShoutAdjustments[yapId];
    let display = Number(originalCount) || 0;
    
    if (localItem && localItem.delta !== undefined) {
      if (localItem.baseCount !== undefined && originalCount !== localItem.baseCount) {
         // Server count has updated (or another user shouted), so we trust the new server count
      } else {
         display = Math.max(0, display + localItem.delta);
      }
    } else {
      const isPersistent = shoutedYaps.includes(yapId);
      if (isPersistent && display === 0) display = 1;
    }
    
    return display;
  };

  const markNotificationAsRead = (notificationId: string) => {
    setLikeNotifications((prev) => prev.map((notif) => notif.id === notificationId ? { ...notif, read: true } : notif));
  };
  const getUnreadNotificationsCount = () => likeNotifications.filter((notif) => !notif.read).length;

  const handleInteraction = ({ postId, postType, authorId, actionType }: InteractionEvent) => {
    if ((userProfile as any).status === 'banned') {
      import('sonner').then(({ toast }) => toast.error('Your account has been suspended.'));
      return;
    }
    if (actionType === 'like') toggleLike(postId, postType);
    else if (actionType === 'shout' && postType === 'yap') toggleShout(postId);
  };

  const isPostLiked = (postId: string, postType: PostType): boolean => {
    return postType === 'shout' ? likedShouts.includes(postId) : likedYaps.includes(postId);
  };

  const getPostLikeCount = (originalCount: number, postId: string, postType: PostType): number => {
    lastSeenLikeCountRef.current[postId] = originalCount;
    const localItem = localLikeAdjustments[postId];
    let display = Number(originalCount) || 0;
    
    if (localItem && localItem.delta !== undefined) {
      if (localItem.baseCount !== undefined && originalCount !== localItem.baseCount) {
         // Server count has updated
      } else {
         display = Math.max(0, display + localItem.delta);
      }
    } else {
      const isPersistent = postType === 'shout' ? likedShouts.includes(postId) : likedYaps.includes(postId);
      if (isPersistent && display === 0) display = 1;
    }
    
    return display;
  };

  const getPostReplyCount = (originalCount: number, postId: string, postType?: PostType): number => {
    lastSeenCommentCountRef.current[postId] = originalCount;
    const localItem = localCommentAdjustments[postId];
    let display = Number(originalCount) || 0;
    
    if (localItem && localItem.delta !== undefined) {
      if (localItem.baseCount !== undefined && originalCount !== localItem.baseCount) {
         // Server updated
      } else {
         display = Math.max(0, display + localItem.delta);
      }
    }
    
    return display;
  };

  const adjustReplyCount = (postId: string, delta: number, postType?: PostType) => {
    const baseCount = lastSeenCommentCountRef.current[postId] || 0;
    setLocalCommentAdjustments(prev => {
        return { 
           ...prev, 
           [postId]: { delta, baseCount, ts: Date.now() } 
        };
    });
  };

  return (
    <YapInteractionsContext.Provider
      value={{
        likedYaps, likedShouts, shoutedYaps, likeNotifications,
        toggleLike, toggleShout, isYapLiked, isYapShouted, getLikeCount, getShoutCount,
        markNotificationAsRead, getUnreadNotificationsCount, handleInteraction,
        isPostLiked, getPostLikeCount, getPostReplyCount, adjustReplyCount,
      }}
    >
      {children}
    </YapInteractionsContext.Provider>
  );
}

export function useYapInteractions() {
  const context = useContext(YapInteractionsContext);
  if (context === undefined) throw new Error('useYapInteractions must be used within a YapInteractionsProvider');
  return context;
}
